import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Car, Utensils, Upload, Check, AlertTriangle, Trash2, Home, Pencil } from 'lucide-react';
import { proofsApi, companyApi, companySettingsApi, type TravelExpense, type BewirtungRow, type CountryOption, type PerDiemRate, type TravelConflictHit } from '../../lib/api';
import { fileToBase64 } from '../receipts/scan';
import { useCompany } from '../CompanyProvider';
import { useConfirm } from '../ConfirmProvider';
import { useToast, errMsg } from '../ToastProvider';
import { Button, Card, EmptyState, ErrorNote, Field, PageHeader, euro } from '../ui';

type Tab = 'travel' | 'bewirtung';

export default function Nachweise(): ReactElement {
  const { active } = useCompany();
  const [tab, setTab] = useState<Tab>('travel');
  const [locked, setLocked] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (!active) return;
    companyApi.usage(active.id).then((u) => setLocked(!u.features.includes('nachweise'))).catch(() => undefined);
  }, [active]);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (locked) {
    return (
      <div className="mx-auto max-w-4xl">
        <PageHeader title="Nachweise" subtitle="Reisekosten, Verpflegungsmehraufwand und Bewirtungsnachweise." />
        <Card className="text-center">
          <p className="text-gray-700 dark:text-gray-300">🔒 Nachweise sind ab dem <strong>Plus</strong>-Tarif verfügbar.</p>
          <Link to="/app/einstellungen/tarif" className="mt-2 inline-block font-medium text-blue-600 hover:underline">Tarif ansehen</Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Nachweise"
        subtitle="Reisekosten (km-Pauschale + Verpflegungsmehraufwand) und Bewirtungsnachweise – rechtssicher."
        action={(
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      />
      <div className="mb-6 flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {([['travel', 'Reisekosten & VMA', Car], ['bewirtung', 'Bewirtung', Utensils]] as [Tab, string, typeof Car][]).map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition ${tab === k ? 'border-blue-600 text-blue-700 dark:text-blue-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <Icon className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>
      {active && tab === 'travel' && <TravelTab companyId={active.id} year={year} />}
      {active && tab === 'bewirtung' && <BewirtungTab companyId={active.id} year={year} />}
    </div>
  );
}

// Adress-Eingabe mit Autovervollstaendigung (ab 3 Zeichen, OSM/Photon via Backend).
// Optionaler Home-Button uebernimmt die Firmenadresse.
function AddressAutocomplete({ companyId, label, value, onChange, placeholder, onHome }: {
  companyId: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string; onHome?: (() => void) | undefined;
}): ReactElement {
  const [hits, setHits] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);

  useEffect(() => {
    function onClick(e: MouseEvent) { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Debounce ~300ms; nur ab 3 Zeichen anfragen.
  useEffect(() => {
    const q = value.trim();
    if (q.length < 3) { setHits([]); return; }
    const id = ++seq.current;
    const t = setTimeout(() => {
      proofsApi.addressSuggest(companyId, q).then((r) => { if (id === seq.current) { setHits(r); setActive(-1); } }).catch(() => undefined);
    }, 300);
    return () => clearTimeout(t);
  }, [value, companyId]);

  function pick(v: string) { onChange(v); setOpen(false); setHits([]); }

  return (
    <div ref={boxRef} className="relative">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        {onHome && (
          <button type="button" onClick={onHome} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline">
            <Home className="h-3.5 w-3.5" /> Firmenadresse
          </button>
        )}
      </div>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { if (hits.length) setOpen(true); }}
        onKeyDown={(e) => {
          if (!open || hits.length === 0) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(hits[active]!); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
      />
      {open && hits.length > 0 && (
        <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {hits.map((h, i) => (
            <li key={h}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(h); }}
                className={`block w-full px-4 py-2 text-left text-sm ${i === active ? 'bg-blue-50 dark:bg-gray-700' : 'hover:bg-blue-50 dark:hover:bg-gray-700'} text-gray-800 dark:text-gray-100`}>
                {h}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Firmenadresse als einzeilige Zeichenkette.
function companyAddressLine(c: { street?: string | null; postalCode?: string | null; city?: string | null }): string {
  return [c.street, [c.postalCode, c.city].filter(Boolean).join(' ')].filter((s) => s && s.trim()).join(', ');
}

function COUNTRY_NAME(countries: CountryOption[], code: string): string {
  return countries.find((c) => c.code === code)?.name ?? code;
}

// ============================ Reisekosten & VMA ============================
function TravelTab({ companyId, year }: { companyId: string; year: number }): ReactElement {
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState<TravelExpense[]>([]);
  const [sums, setSums] = useState({ kmTotalCents: 0, vmaTotalCents: 0, lodgingTotalCents: 0, totalCents: 0 });
  const [error, setError] = useState<unknown>(null);
  const [mapsOk, setMapsOk] = useState(false);
  const [companyAddr, setCompanyAddr] = useState('');
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [showNew, setShowNew] = useState(false);
  // Gesetzte id = Bearbeiten-Modus (sonst Neuanlage).
  const [editId, setEditId] = useState<string | null>(null);
  // Doppelabzug-Warnung: Belege am Reisetag, die mit der Pauschale kollidieren.
  const [conflicts, setConflicts] = useState<TravelConflictHit[]>([]);
  // Konflikte je erfasster Reise (Markierung in der Liste) + aufgeklappte Zeile.
  const [rowConflicts, setRowConflicts] = useState<Record<string, TravelConflictHit[]>>({});
  const [expanded, setExpanded] = useState<string | null>(null);

  // Formular
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Zwischenstopps in Reihenfolge: Start -> stops[0] -> ... -> Ziel.
  // Viele Reisen gehen nicht von A nach B, sondern Heim -> Kunde 1 ->
  // Kunde 2 -> Heim; die Pauschale bemisst sich an der Gesamtstrecke.
  const [stops, setStops] = useState<string[]>([]);
  const [country, setCountry] = useState('DE');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState<CountryOption[]>([]);
  const [distance, setDistance] = useState('');
  const [roundTrip, setRoundTrip] = useState(true);
  const [multiDay, setMultiDay] = useState(false);
  const [hours, setHours] = useState('');
  const [tripStart, setTripStart] = useState('');
  const [tripEnd, setTripEnd] = useState('');
  const [lodging, setLodging] = useState('');
  const [purpose, setPurpose] = useState('');
  const [rate, setRate] = useState<PerDiemRate | null>(null);
  const [rateApprox, setRateApprox] = useState(false);
  const [calcBusy, setCalcBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  function load() {
    proofsApi.travelList(companyId, year).then((r) => {
      setItems(r.items);
      setSums({ kmTotalCents: r.kmTotalCents, vmaTotalCents: r.vmaTotalCents, lodgingTotalCents: r.lodgingTotalCents, totalCents: r.totalCents });
      setRowConflicts(r.conflicts ?? {});
    }).catch(setError);
  }
  useEffect(() => { load(); proofsApi.mapsAvailable(companyId).then(setMapsOk).catch(() => undefined); /* eslint-disable-next-line */ }, [companyId, year]);
  useEffect(() => { companySettingsApi.get(companyId).then((c) => setCompanyAddr(companyAddressLine(c))).catch(() => undefined); }, [companyId]);
  useEffect(() => { proofsApi.perdiem(companyId).then((r) => setCountries(r.countries)).catch(() => undefined); }, [companyId]);

  // Pauschsatz fuer Land + Reisejahr laden (Vorschau hinterlegt korrekte BMF-Saetze).
  const travelYear = useMemo(() => Number((multiDay ? tripStart : date).slice(0, 4)) || year, [multiDay, tripStart, date, year]);
  useEffect(() => {
    proofsApi.perdiem(companyId, { country, city: city || undefined, year: travelYear })
      .then((r) => { setRate(r.rate ?? null); setRateApprox(!!r.approx); setCities(r.cities ?? []); })
      .catch(() => undefined);
  }, [companyId, country, city, travelYear]);
  // Stadt zuruecksetzen, wenn das Land gewechselt wird.
  useEffect(() => { setCity(''); }, [country]);

  // VMA-Vorschau mit echten Land/Jahr-Saetzen.
  const vmaPreview = useMemo(() => {
    if (!rate) return 0;
    if (multiDay) {
      if (!tripStart || !tripEnd) return 0;
      const s = Date.parse(tripStart), e = Date.parse(tripEnd);
      if (isNaN(s) || isNaN(e) || e < s) return 0;
      const days = Math.round((e - s) / 86_400_000) + 1;
      if (days <= 1) return rate.full;
      if (days === 2) return rate.partial * 2;
      return rate.partial * 2 + rate.full * (days - 2);
    }
    const h = parseFloat(hours.replace(',', '.')) || 0;
    return h > 8 ? rate.partial : 0;
  }, [rate, multiDay, tripStart, tripEnd, hours]);
  const kmPreview = useMemo(() => {
    const d = parseFloat(distance.replace(',', '.')) || 0;
    return Math.round(d * (roundTrip ? 2 : 1) * 30);
  }, [distance, roundTrip]);
  const lodgingPreview = useMemo(() => Math.round((parseFloat(lodging.replace(',', '.')) || 0) * 100), [lodging]);

  async function calcDistance() {
    if (!from.trim() || !to.trim()) { toast.error('Bitte Start und Ziel angeben.'); return; }
    setCalcBusy(true);
    try {
      const r = await proofsApi.distance(companyId, from, to, stops.filter((s) => s.trim()));
      setDistance(String(r.distanceKm).replace('.', ','));
      toast.success(
        r.etappen && r.etappen.length > 1
          ? `Gesamtstrecke: ${r.distanceKm} km über ${r.etappen.length} Etappen (einfach).`
          : `Entfernung: ${r.distanceKm} km (einfach).`,
      );
    } catch (e) { toast.error(errMsg(e)); } finally { setCalcBusy(false); }
  }

  function resetForm() {
    setEditId(null); setConflicts([]);
    setFrom(''); setTo(''); setStops([]); setCountry('DE'); setCity(''); setDistance(''); setRoundTrip(true);
    setMultiDay(false); setDate(new Date().toISOString().slice(0, 10));
    setHours(''); setTripStart(''); setTripEnd(''); setLodging(''); setPurpose('');
  }

  // Bestehende Reise ins Formular laden (Bearbeiten).
  function startEdit(it: TravelExpense) {
    setEditId(it.id);
    setFrom(it.fromAddress); setTo(it.toAddress); setStops(it.stops ?? []);
    setCountry(it.country || 'DE'); setCity(it.city ?? '');
    setDistance(String(it.distanceKm).replace('.', ','));
    setRoundTrip(it.roundTrip);
    const isMulti = !!(it.tripStart && it.tripEnd);
    setMultiDay(isMulti);
    setDate(it.date.slice(0, 10));
    setTripStart(it.tripStart ? it.tripStart.slice(0, 10) : '');
    setTripEnd(it.tripEnd ? it.tripEnd.slice(0, 10) : '');
    setHours(it.hoursAway != null ? String(it.hoursAway).replace('.', ',') : '');
    setLodging(it.lodgingCents ? (it.lodgingCents / 100).toFixed(2).replace('.', ',') : '');
    setPurpose(it.purpose ?? '');
    setShowNew(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Doppelabzug-Pruefung: Belege am Reisetag suchen, sobald Datum/Uebernachtung
  // stehen. Nur ein Hinweis — gesetzlich gilt km-Pauschale ODER tatsaechliche
  // Fahrtkosten, nie beides. Die eigene Reise ist kein Beleg, also kein Selbsttreffer.
  useEffect(() => {
    if (!showNew) { setConflicts([]); return; }
    const d = multiDay ? tripStart : date;
    if (!d) { setConflicts([]); return; }
    let alive = true;
    const t = setTimeout(() => {
      proofsApi.travelConflicts(companyId, {
        date: d,
        tripStart: multiDay ? tripStart : null,
        tripEnd: multiDay ? tripEnd : null,
        lodgingCents: lodgingPreview,
      }).then((h) => { if (alive) setConflicts(h); }).catch(() => { if (alive) setConflicts([]); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [companyId, showNew, multiDay, date, tripStart, tripEnd, lodgingPreview]);

  async function save() {
    const km = parseFloat(distance.replace(',', '.')) || 0;
    if (!from.trim() || !to.trim() || km <= 0) { toast.error('Start, Ziel und Entfernung sind nötig.'); return; }
    if (multiDay && (!tripStart || !tripEnd)) { toast.error('Bitte Reisebeginn und -ende angeben.'); return; }
    // Bei moeglichem Doppelabzug einmal nachfragen — der Nutzer weiss, ob der
    // Beleg zu dieser Fahrt gehoert oder zu einer anderen.
    if (conflicts.length > 0) {
      const ok = await confirm({
        title: 'Mögliche Doppelerfassung',
        message: `Am Reisetag ${conflicts.length === 1 ? 'liegt ein Beleg' : `liegen ${conflicts.length} Belege`} vor, der mit den Pauschalen kollidieren kann (z. B. Tank- oder Verpflegungsbeleg). Absetzbar ist entweder die Pauschale oder der tatsächliche Aufwand — nicht beides. Trotzdem speichern?`,
        confirmLabel: 'Trotzdem speichern',
      });
      if (!ok) return;
    }
    setBusy(true); setError(null);
    const input = {
      date: multiDay ? tripStart : date,
      fromAddress: from.trim(), toAddress: to.trim(), stops: stops.map((s) => s.trim()).filter(Boolean), country, city: city || null, distanceKm: km, roundTrip,
      hoursAway: !multiDay && hours.trim() ? (parseFloat(hours.replace(',', '.')) || 0) : null,
      tripStart: multiDay ? tripStart : null,
      tripEnd: multiDay ? tripEnd : null,
      lodgingCents: lodgingPreview || null,
      purpose: purpose.trim() || null,
    };
    try {
      if (editId) await proofsApi.travelUpdate(companyId, editId, input);
      else await proofsApi.travelCreate(companyId, input);
      toast.success(editId ? 'Reise aktualisiert.' : 'Reise gespeichert.');
      setShowNew(false); resetForm();
      load();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }

  async function del(id: string) {
    const ok = await confirm({ title: 'Reise löschen?', message: 'Der Eintrag wird entfernt.', confirmLabel: 'Löschen' });
    if (!ok) return;
    try { await proofsApi.travelDelete(companyId, id); load(); } catch (e) { toast.error(errMsg(e)); }
  }

  return (
    <div className="space-y-4">
      <ErrorNote error={error} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card className="!p-4"><div className="text-xs uppercase tracking-wide text-gray-400">Kilometerpauschale</div><div className="mt-1 text-xl font-bold">{euro(sums.kmTotalCents)}</div></Card>
        <Card className="!p-4"><div className="text-xs uppercase tracking-wide text-gray-400">Verpflegungsmehraufwand</div><div className="mt-1 text-xl font-bold">{euro(sums.vmaTotalCents)}</div></Card>
        <Card className="!p-4"><div className="text-xs uppercase tracking-wide text-gray-400">Übernachtung</div><div className="mt-1 text-xl font-bold">{euro(sums.lodgingTotalCents)}</div></Card>
        <Card className="!p-4"><div className="text-xs uppercase tracking-wide text-gray-400">Summe {year}</div><div className="mt-1 text-xl font-bold text-emerald-600">{euro(sums.totalCents)}</div></Card>
      </div>
      <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-950/30 dark:text-blue-300">
        Die Beträge wirken <strong>sofort als Betriebsausgabe</strong> in EÜR und BWA – du musst nichts weiter übernehmen.
        Beim <strong>Jahresabschluss</strong> wird die Jahressumme zusätzlich als Buchung im Journal erfasst (Reisekosten gegen Privateinlage, da du privat verauslagt hast).
      </p>

      <div className="flex justify-end">
        <Button onClick={() => { if (showNew) { setShowNew(false); resetForm(); } else { resetForm(); setShowNew(true); } }}>
          {showNew ? 'Abbrechen' : '+ Reise erfassen'}
        </Button>
      </div>

      {showNew && (
        <Card className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <AddressAutocomplete companyId={companyId} label="Start" value={from} onChange={setFrom} placeholder="z. B. Musterstr. 1, Berlin"
              onHome={companyAddr ? () => setFrom(companyAddr) : undefined} />
            <AddressAutocomplete companyId={companyId} label="Ziel" value={to} onChange={setTo} placeholder="z. B. Hauptstr. 5, Hamburg" />
          </div>

          {/* Zwischenstopps: Reisen gehen oft Heim -> A -> B -> Heim.
              Die Pauschale bemisst sich an der Gesamtstrecke. */}
          {stops.length > 0 && (
            <div className="space-y-2">
              {stops.map((s, i) => (
                <div key={i} className="flex items-end gap-2">
                  <div className="flex-1">
                    <AddressAutocomplete
                      companyId={companyId}
                      label={`Zwischenstopp ${i + 1}`}
                      value={s}
                      onChange={(v) => setStops(stops.map((x, j) => (j === i ? v : x)))}
                      placeholder="z. B. Kundenadresse"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setStops(stops.filter((_, j) => j !== i))}
                    aria-label={`Zwischenstopp ${i + 1} entfernen`}
                    className="mb-1 rounded-lg p-2.5 text-gray-400 transition hover:bg-gray-100 hover:text-rose-600 dark:hover:bg-gray-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {stops.length < 10 && (
            <button
              type="button"
              onClick={() => setStops([...stops, ''])}
              className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              + Zwischenstopp {stops.length > 0 ? 'hinzufügen' : '(z. B. Heim → Kunde 1 → Kunde 2 → Heim)'}
            </button>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Zielland (für Verpflegungspauschale)</span>
              <select value={country} onChange={(e) => setCountry(e.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-base outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </label>
            {cities.length > 0 && (
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Stadt (eigener Satz)</span>
                <select value={city} onChange={(e) => setCity(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-base outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                  <option value="">im Übrigen (Landessatz)</option>
                  {cities.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </label>
            )}
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-1"><Field label="Entfernung (km, einfach)" value={distance} onChange={setDistance} placeholder="z. B. 289" /></div>
            {mapsOk && <Button variant="secondary" onClick={calcDistance} busy={calcBusy}>per Maps berechnen</Button>}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} className="h-4 w-4" />
            Hin- und Rückfahrt (Entfernung × 2)
          </label>
          {/* Rundreise erkannt: Start = Ziel. Dann ist die Rueckfahrt schon
              in der Strecke enthalten — ein Haekchen wuerde sie verdoppeln. */}
          {roundTrip && stops.length > 0 && from.trim() && from.trim().toLowerCase() === to.trim().toLowerCase() && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Deine Route endet dort, wo sie beginnt – die Rückfahrt ist also schon enthalten.
              Mit gesetztem Haken wird die Strecke doppelt angesetzt.
            </p>
          )}

          {/* eintägig vs. mehrtägig */}
          <div className="flex gap-2">
            <button type="button" onClick={() => setMultiDay(false)} className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${!multiDay ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600'}`}>Eintägig</button>
            <button type="button" onClick={() => setMultiDay(true)} className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${multiDay ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600'}`}>Mehrtägig</button>
          </div>

          {multiDay ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Reisebeginn" type="date" value={tripStart} onChange={setTripStart} />
              <Field label="Reiseende" type="date" value={tripEnd} onChange={setTripEnd} />
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Datum" type="date" value={date} onChange={setDate} />
              <Field label="Abwesenheit (Stunden)" value={hours} onChange={setHours} placeholder="z. B. 8,5" hint="mehr als 8 h nötig (8 h exakt = 0 €)" />
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Übernachtungskosten € (tatsächlich, mit Beleg)" value={lodging} onChange={setLodging} placeholder="z. B. 117,00" hint="Nur echte Hotelkosten — keine Pauschale (Unternehmer)." />
            <Field label="Anlass / Zweck" value={purpose} onChange={setPurpose} placeholder="z. B. Kundentermin Müller GmbH" />
          </div>

          {rate && (
            <p className="text-xs text-gray-400">
              {COUNTRY_NAME(countries, country)} {travelYear}: voller Tag {euro(rate.full)} · Teiltag/&gt;8h {euro(rate.partial)} · Übernachtungspauschale {euro(rate.lodging)} (nur Richtwert)
              {rateApprox && ' · Hinweis: 2023-Sätze als Näherung'}
            </p>
          )}

          <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-gray-800">
            Vorschau: Kilometer <strong>{euro(kmPreview)}</strong> · Verpflegung <strong>{euro(vmaPreview)}</strong> · Übernachtung <strong>{euro(lodgingPreview)}</strong> · Summe <strong>{euro(kmPreview + vmaPreview + lodgingPreview)}</strong>
          </div>

          {/* Doppelabzug: Pauschale ODER tatsaechliche Kosten, nie beides. */}
          {conflicts.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold">Achtung: mögliche Doppelerfassung</div>
                  <p className="mt-0.5">
                    Absetzbar ist entweder die Pauschale <em>oder</em> der tatsächliche Aufwand — nicht beides.
                    Diese Belege liegen im Reisezeitraum:
                  </p>
                  <ul className="mt-1.5 space-y-1">
                    {conflicts.map((c) => (
                      <li key={c.receiptId}>
                        <strong>{new Date(c.date).toLocaleDateString('de-DE')}</strong>
                        {c.vendor ? ` · ${c.vendor}` : ''} · {euro(c.grossCents)}
                        <span className="block text-amber-800/80 dark:text-amber-300/70">{c.reason}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-amber-800/80 dark:text-amber-300/70">
                    Gehören die Belege zu einer anderen Fahrt, kannst du normal speichern.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            {editId && <Button variant="secondary" onClick={() => { setShowNew(false); resetForm(); }}>Abbrechen</Button>}
            <Button onClick={save} busy={busy}>{editId ? 'Änderungen speichern' : 'Speichern'}</Button>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState title="Noch keine Reisen erfasst" hint="Erfasse Dienstreisen – Entfernung per Google Maps, Verpflegungsmehraufwand automatisch." />
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const hits = rowConflicts[it.id] ?? [];
            const open = expanded === it.id;
            return (
            <Card key={it.id} className={`!p-4 ${hits.length ? 'border-amber-300 dark:border-amber-800' : ''}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {/* Mit Stopps die ganze Route zeigen — sonst sieht eine
                        Rundreise wie eine einfache Fahrt aus. */}
                    <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                      {[it.fromAddress, ...(it.stops ?? []), it.toAddress].join(' → ')}
                    </span>
                    {hits.length > 0 && (
                      <button type="button" onClick={() => setExpanded(open ? null : it.id)}
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:hover:bg-amber-900/60"
                        aria-expanded={open}
                        title="Belege im Reisezeitraum, die mit den Pauschalen kollidieren könnten">
                        <AlertTriangle className="h-3 w-3" />
                        {hits.length} {hits.length === 1 ? 'Beleg' : 'Belege'}
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {it.tripStart && it.tripEnd
                      ? `${new Date(it.tripStart).toLocaleDateString('de-DE')}–${new Date(it.tripEnd).toLocaleDateString('de-DE')}`
                      : new Date(it.date).toLocaleDateString('de-DE')}
                    {it.country && it.country !== 'DE' ? ` · ${COUNTRY_NAME(countries, it.country)}` : ''}
                    {' · '}{String(it.distanceKm)} km {it.roundTrip ? '(Hin/Rück)' : '(einfach)'}
                    {!it.tripStart && it.hoursAway ? ` · ${it.hoursAway} h` : ''}
                    {it.lodgingCents ? ` · Übern. ${euro(it.lodgingCents)}` : ''}{it.purpose ? ` · ${it.purpose}` : ''}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <div className="text-right">
                    <div className="font-semibold">{euro(it.kmAmountCents + it.vmaCents + it.lodgingCents)}</div>
                    <div className="text-xs text-gray-400">km {euro(it.kmAmountCents)}{it.vmaCents ? ` · VMA ${euro(it.vmaCents)}` : ''}{it.lodgingCents ? ` · Ü ${euro(it.lodgingCents)}` : ''}</div>
                  </div>
                  <button onClick={() => startEdit(it)} className="text-gray-400 hover:text-blue-600" aria-label="Bearbeiten"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(it.id)} className="text-gray-400 hover:text-rose-600" aria-label="Löschen"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>

              {/* Aufgeklappt: die Belege dieses Reisezeitraums im Detail. */}
              {open && hits.length > 0 && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/20">
                  <div className="flex items-start gap-2 text-xs text-amber-900 dark:text-amber-200">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>
                      Diese Belege liegen im Reisezeitraum. Absetzbar ist entweder die <strong>Pauschale</strong> oder
                      der <strong>tatsächliche Aufwand</strong> — nicht beides. Gehören sie zu einer anderen Fahrt, ist alles in Ordnung.
                    </p>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {hits.map((h) => (
                      <div key={h.receiptId} className="flex items-start justify-between gap-3 rounded-md bg-white/70 px-2.5 py-1.5 text-xs dark:bg-gray-900/40">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {new Date(h.date).toLocaleDateString('de-DE')} · {h.vendor ?? 'Ohne Händler'}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400">
                            {h.category ?? 'Ohne Kategorie'}{h.accountNumber ? ` · Konto ${h.accountNumber}` : ''}
                          </div>
                          <div className="mt-0.5 text-amber-800 dark:text-amber-300/80">{h.reason}</div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="font-semibold text-gray-900 dark:text-gray-100">{euro(h.grossCents)}</div>
                          <Link to="/belege" className="text-blue-600 hover:underline dark:text-blue-400">zu den Belegen</Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================ Bewirtung ============================
function BewirtungTab({ companyId, year }: { companyId: string; year: number }): ReactElement {
  const toast = useToast();
  const [rows, setRows] = useState<BewirtungRow[]>([]);
  const [summary, setSummary] = useState({ total: 0, missing: 0 });
  const [error, setError] = useState<unknown>(null);
  const [edit, setEdit] = useState<BewirtungRow | null>(null);

  function load() {
    proofsApi.bewirtungList(companyId, year).then((r) => { setRows(r.rows); setSummary({ total: r.total, missing: r.missing }); }).catch(setError);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, year]);

  return (
    <div className="space-y-4">
      <ErrorNote error={error} />
      <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-950/30 dark:text-blue-300">
        Jeder als <strong>Bewirtung</strong> markierte Beleg braucht einen Nachweis (Anlass + Teilnehmer). Du kannst ihn rechtssicher hier ausfüllen <strong>oder</strong> ein Foto des handschriftlichen Belegs hochladen.
      </p>

      {rows.length === 0 ? (
        <EmptyState title="Keine Bewirtungsbelege" hint={`Für ${year} sind keine Belege als „Bewirtung" markiert. Markiere einen Beleg unter „Belege".`} />
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm">
            {summary.missing === 0
              ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 font-medium text-emerald-700"><Check className="h-4 w-4" /> Alle {summary.total} Nachweise vollständig</span>
              : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 font-medium text-amber-700"><AlertTriangle className="h-4 w-4" /> {summary.missing} von {summary.total} Nachweisen fehlen</span>}
          </div>
          <div className="space-y-2">
            {rows.map((r) => (
              <Card key={r.receiptId} className={`flex items-center justify-between gap-3 !p-4 ${r.hasProof ? '' : 'ring-1 ring-amber-200'}`}>
                <div className="min-w-0">
                  <div className="truncate font-medium text-gray-900 dark:text-gray-100">{r.vendor || 'Bewirtung'}</div>
                  <div className="text-xs text-gray-500">{new Date(r.date).toLocaleDateString('de-DE')} · {euro(r.grossCents)}
                    {r.hasProof ? <span className="ml-2 text-emerald-600">✓ Nachweis ({r.proof?.mode === 'uploaded' ? 'Foto' : 'erstellt'})</span>
                      : <span className="ml-2 text-amber-600">Nachweis fehlt</span>}
                  </div>
                </div>
                <Button variant={r.hasProof ? 'secondary' : 'primary'} onClick={() => setEdit(r)}>{r.hasProof ? 'Bearbeiten' : 'Nachweis'}</Button>
              </Card>
            ))}
          </div>
        </>
      )}

      {edit && (
        <BewirtungModal companyId={companyId} row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} onError={(e) => toast.error(errMsg(e))} />
      )}
    </div>
  );
}

function BewirtungModal({ companyId, row, onClose, onSaved, onError }: { companyId: string; row: BewirtungRow; onClose: () => void; onSaved: () => void; onError: (e: unknown) => void }): ReactElement {
  const [mode, setMode] = useState<'created' | 'uploaded'>(row.proof?.mode ?? 'created');
  const [occasion, setOccasion] = useState(row.proof?.occasion ?? '');
  const [participants, setParticipants] = useState(row.proof?.participants ?? '');
  const [location, setLocation] = useState(row.proof?.location ?? '');
  const [busy, setBusy] = useState(false);

  async function saveCreated() {
    if (!occasion.trim() || !participants.trim()) { onError(new Error('Anlass und Teilnehmer sind Pflicht (Rechtssicherheit).')); return; }
    setBusy(true);
    try { await proofsApi.bewirtungSave(companyId, row.receiptId, { occasion: occasion.trim(), participants: participants.trim(), location: location.trim() }); onSaved(); }
    catch (e) { onError(e); } finally { setBusy(false); }
  }
  async function upload(file: File) {
    setBusy(true);
    try { const data = await fileToBase64(file); await proofsApi.bewirtungUpload(companyId, row.receiptId, data, file.type || 'image/jpeg'); onSaved(); }
    catch (e) { onError(e); } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6 dark:bg-gray-800">
        <h2 className="mb-1 text-lg font-bold text-gray-900 dark:text-gray-100">Bewirtungsnachweis</h2>
        <p className="mb-4 text-sm text-gray-500">{row.vendor || 'Bewirtung'} · {new Date(row.date).toLocaleDateString('de-DE')} · {euro(row.grossCents)}</p>

        <div className="mb-4 flex gap-2">
          <button onClick={() => setMode('created')} className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${mode === 'created' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600'}`}>Rechtssicher erstellen</button>
          <button onClick={() => setMode('uploaded')} className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${mode === 'uploaded' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600'}`}>Foto hochladen</button>
        </div>

        {mode === 'created' ? (
          <div className="space-y-3">
            <Field label="Anlass der Bewirtung *" value={occasion} onChange={setOccasion} placeholder="z. B. Geschäftsessen Projektbesprechung" />
            <Field label="Bewirtete Personen *" value={participants} onChange={setParticipants} placeholder="z. B. Max Müller (Kunde), ich" hint="Alle Teilnehmer namentlich." />
            <Field label="Ort / Gaststätte" value={location} onChange={setLocation} placeholder="z. B. Restaurant Adler, Berlin" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
              <Button onClick={saveCreated} busy={busy}>Speichern</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {row.proof?.mode === 'uploaded' && (
              <img src={`/api/proofs/bewirtung/${row.receiptId}/file`} alt="Nachweis" className="max-h-48 rounded-lg border border-gray-200" />
            )}
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 py-8 text-sm text-gray-500 hover:border-blue-400">
              <Upload className="h-6 w-6" />
              {busy ? 'Wird hochgeladen …' : 'Foto des Bewirtungsbelegs wählen'}
              <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} />
            </label>
            <p className="text-xs text-gray-400">Das Bild wird automatisch komprimiert gespeichert.</p>
            <div className="flex justify-end"><Button variant="secondary" onClick={onClose}>Schließen</Button></div>
          </div>
        )}
      </div>
    </div>
  );
}
